// @mui
import {
  Stack,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
// hooks
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import ConfirmationDialog from '../../shared/ConfirmationDialog';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { fDate } from 'src/utils/formatTime';
import { EventModal } from './EventModal';
import { useEvents } from '../useEvents';
import { Event } from 'src/@types/event';

// ----------------------------------------------------------------------

export default function EventsList() {
  const navigate = useNavigate();
  const {
    events,
    modalOpen,
    setModalOpen,
    currentEvent,
    setCurrentEvent,
    getList,
    handleEditRow,
    handleDeleteRow,
  } = useEvents();

  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });

  useEffect(() => {
    getList().catch(console.error);
  }, []);

  return (
    <Stack>
      <HeaderBreadcrumbs
        heading="Programs"
        links={[
          { name: 'Dashboard', href: PATH_DASHBOARD.root },
          { name: 'Programs', href: PATH_DASHBOARD.events },
        ]}
        action={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => navigate(PATH_DASHBOARD.programQuizAnalysis)}
              startIcon={<Iconify icon={'eva:pie-chart-outline'} />}
            >
              Quiz Analysis
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setCurrentEvent(undefined);
                setModalOpen(true);
              }}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              New Program
            </Button>
          </Stack>
        }
      />

      <Card>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Banner</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Lectures</TableCell>
                <TableCell align="center">Batches</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event: Event) => (
                <TableRow key={event._id} hover>
                  <TableCell>
                    {event.bannerImage ? (
                      <Image
                        alt={event.title}
                        src={event.bannerImage}
                        sx={{ width: 64, height: 48, borderRadius: 1 }}
                      />
                    ) : (
                      <Typography variant="caption" color={'text.disabled'}>
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{event.title}</Typography>
                    {event.subtitle && (
                      <Typography variant="caption" color={'text.secondary'}>
                        {event.subtitle}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={'text.secondary'}>
                      {event.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>{event.date ? fDate(event.date) : '—'}</TableCell>
                  <TableCell align="center">{event.lectures?.length || 0}</TableCell>
                  <TableCell align="center">{event.batches?.length || 0}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={event.active ? 'Active' : 'Inactive'}
                      color={event.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction={'row'} spacing={0.5} justifyContent={'flex-end'}>
                      <Tooltip title="Batches & quiz reports">
                        <IconButton
                          size="small"
                          onClick={() => navigate(PATH_DASHBOARD.programBatches(event._id))}
                        >
                          <Iconify icon={'eva:people-outline'} />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => handleEditRow(event)}>
                        <Iconify icon={'eva:edit-fill'} />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: 'error.main' }}
                        onClick={() => setConfirmOpen({ open: true, currentId: event._id })}
                      >
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Stack height="200px" alignItems={'center'} justifyContent="center">
                      <Typography>No programs yet</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <EventModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          getList();
          setCurrentEvent(undefined);
        }}
        isEdit={currentEvent ? true : false}
        currentEvent={currentEvent}
      />

      <ConfirmationDialog
        headText={'Delete Program'}
        text={'Are You Sure You Need To Delete This Program'}
        isOpen={confirmOpen.open}
        onClose={() => {
          setConfirmOpen({ open: false, currentId: '' });
          getList();
        }}
        confirmAction={() => {
          handleDeleteRow(confirmOpen.currentId).then(() => getList());
        }}
        loading={false}
      />
    </Stack>
  );
}
